import { NgIf } from '@angular/common';
import { Component, OnInit, computed, inject, input, output } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
	DividerComponent,
	LoadingSpinnerComponent,
	TextComponent,
} from '@zeiss/beyond-online-angular';
import { BehaviorSubject } from 'rxjs';
import { LoadingAnimationDirective } from 'src/app/modules/loading-animation/loading-animation.directive';
import { ConfigurationStore } from 'src/app/state-management/stores/configuration.store';
import { ECPStore } from 'src/app/state-management/stores/ecp.store';
import { OrderStore } from 'src/app/state-management/stores/order.store';
import { PersonalizationStore } from 'src/app/state-management/stores/personalization.store';
import { ProductsStore } from 'src/app/state-management/stores/products.store';
import { IconComponent, PopUpService, SimpleInfoPopupComponent } from 'vis-ods-angular';
import { ageGroupArray } from '../../../pages/order/age-group-select/age-group-array';
import { RxVrxDisplayComponent } from '../rx-vrx-display/rx-vrx-display.component';

@Component({
	selector: 'app-product-summary',
	templateUrl: './product-summary.component.html',
	imports: [
		TranslocoModule,
		IconComponent,
		RxVrxDisplayComponent,
		NgIf,
		TextComponent,
		LoadingAnimationDirective,
		LoadingSpinnerComponent,
		DividerComponent,
	],
})
export class ProductSummaryComponent implements OnInit {
	private readonly configurationStore = inject(ConfigurationStore);
	private readonly personalizationStore = inject(PersonalizationStore);
	private readonly ecpStore = inject(ECPStore);
	private readonly orderStore = inject(OrderStore);
	private readonly productsStore = inject(ProductsStore);
	private readonly popUpService = inject(PopUpService);
	private readonly translocoService = inject(TranslocoService);

	public $selectedSoldToStore = this.ecpStore.selectedSoldToStore;
	public $shipToStores = this.ecpStore.shipToStores;
	public $colors = this.personalizationStore.colors;
	public $settings = this.configurationStore.settings;
	public $order = this.orderStore.order;
	public $product = this.productsStore.selectedProductDetails;
	public $isReader = this.productsStore.isReader;
	public $displayVrx = this.orderStore.displayVrx;
	public $vrxValues = this.orderStore.vrxValues;
	public $vrxValuesLoading = this.orderStore.isLoading('vrxValues');
	public $contactPersonsLoading = this.ecpStore.isLoading('shipToContacts');
	public $shipToStoresLoading = this.ecpStore.isLoading('shipToStores');

	public vrxReasonKey = computed(() => {
		const vrxData = this.$vrxValues();

		if (!vrxData) return '';

		return vrxData.left?.addition != undefined || vrxData.right?.addition != undefined
			? 'productSummary.texts.vrxAppliedBecauseOfAddition'
			: 'productSummary.texts.vrxAppliedBecauseOfAgeGroup';
	});

	public $rx = computed(() => {
		const prescriptionsFilled =
			this.$order().prescriptions?.left?.sphere != null &&
			this.$order().prescriptions?.right?.sphere != null;

		if (prescriptionsFilled && this.$product()?.id === 1) {
			return this.$order().prescriptions;
		} else {
			const cylinderRanges = this.$product()?.rx?.sphere?.cylinderRanges;
			const sphere = cylinderRanges ? cylinderRanges[0].sphere : null;
			return { left: { sphere }, right: { sphere } };
		}
	});

	public $vrx = computed(() => {
		return this.$displayVrx() ? this.$vrxValues() : null;
	});

	public readonly $editShippingAddressEnabled = input(true, {
		alias: 'editShippingAddressEnabled',
	});
	public readonly $editContactPersonEnabled = input(true, { alias: 'editContactPersonEnabled' });
	public readonly editShippingAddressRequested = output<boolean>();
	public readonly editContactPersonRequested = output<boolean>();

	async ngOnInit() {
		const productId = this.$order().product;
		if (productId && this.$product()?.id !== productId) {
			await this.productsStore.fetchProduct(productId);
		}
		if (this.$shipToStores().length === 0) {
			await this.ecpStore.fetchShipToStores();
		}

		if (!this.$order().shipTo) {
			this.setDefaultShipTo();
		}

		if (this.$vrxValues() === null && this.$order().product === 1) {
			await this.orderStore.fetchVrxValues({
				consumerAge: this.$order().ageGroup,
				left: this.$order().prescriptions?.left,
				right: this.$order().prescriptions?.right,
			});
		}
		if (this.configurationStore.settings()?.color_dot && this.$order().color?.colorCode != null) {
			await this.personalizationStore.fetchColors();
		}
	}

	editShippingAddress() {
		this.editShippingAddressRequested.emit(true);
	}

	editContactPerson() {
		this.editContactPersonRequested.emit(true);
	}

	getAgeGroupLabel(ageGroupName: number | string | undefined): string {
		if (!ageGroupName) {
			return '';
		}
		const correctAgeGroup = ageGroupArray.find(
			(ageGroup) => ageGroup.name === ageGroupName.toString(),
		);
		return correctAgeGroup ? correctAgeGroup.label : '';
	}

	openVRXInformationPopup() {
		this.popUpService.open(SimpleInfoPopupComponent, {
			enableCloseButton: new BehaviorSubject<boolean>(true),
			data: {
				title: this.translocoService.translate(
					'productSummary.subComponents.vrxInformationPopup.headlines.title',
				),
				texts: [
					{
						text: this.translocoService.translate(
							'productSummary.subComponents.vrxInformationPopup.texts.message',
						),
					},
				],
			},
		});
	}

	private setDefaultShipTo() {
		if (this.$shipToStores().length === 1) {
			this.orderStore.setShippingAddress(this.$shipToStores()[0], 'B2B');
		} else {
			const primaryShipToStore = this.$shipToStores().find((store) => store.isPrimary);
			if (primaryShipToStore) {
				this.orderStore.setShippingAddress(primaryShipToStore, 'B2B');
			}
		}
	}
}
